import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import clsx from "clsx";
import { RightSidebarNarrative } from "./rightSidebarNarrative";
import { type RightSidebarContentType } from "./types";
import { PDFViewer } from "@/components/feature/pdf/pdfViewer";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { RightDrawerDocumentName } from "@/components/feature/layout/right-sidebar/components/documentName";
import type { DocumentChronology } from "@/types/chronology";

interface RightDrawerProps {
  isVisible: boolean;
  toggleVisibility: () => void;
  contentType: RightSidebarContentType;
  setContentType: (contentType: RightSidebarContentType) => void;
  currentPdfId: string | null;
  documentChronology?: DocumentChronology;
}

export const RightSidebar: React.FC<RightDrawerProps> = ({
  currentPdfId,
  isVisible,
  documentChronology,
  toggleVisibility,
  contentType,
  setContentType,
}) => {
  const { currentCitation } = useCurrentPdf();
  const width = React.useMemo(() => {
    if (!isVisible) return "48px";
    if (contentType === "pdf" || contentType === "narrative") return "60%";
    return "0%";
  }, [isVisible, contentType]);

  return (
    <motion.div className="relative" initial={false} animate={{ width }} transition={{ duration: 0.3 }}>
      <div className="absolute inset-0 flex flex-col bg-ui-sidebar-background">
        <div className="flex items-center border-b border-gray-200 bg-ui-sidebar-background p-2">
          <button onClick={toggleVisibility} className="mr-2 rounded-full p-2 hover:bg-gray-200">
            {isVisible ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
          </button>
          {isVisible && (
            <div className="flex space-x-2">
              <button
                onClick={() => setContentType("narrative")}
                className={clsx("flex items-center rounded p-2", {
                  "bg-blue-100": contentType === "narrative",
                  "hover:bg-gray-200": contentType !== "narrative",
                })}
              >
                <FileText size={20} className="mr-2" />
                <span>Narrative</span>
              </button>
              <button
                onClick={() => setContentType("pdf")}
                className={clsx("flex items-center rounded p-2", {
                  "bg-blue-100": contentType === "pdf",
                  "hover:bg-gray-200": contentType !== "pdf",
                })}
              >
                <FileText size={20} className="mr-2" />
                <span>PDF</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex-grow overflow-auto">
          <AnimatePresence>
            {isVisible && (
              <motion.div
                className="h-full w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {currentPdfId && contentType === "pdf" && (
                  <>
                    <RightDrawerDocumentName />
                    <PDFViewer citationHighlight={currentCitation} />
                  </>
                )}
                {documentChronology && contentType === "narrative" && (
                  <RightSidebarNarrative documentChronology={documentChronology} />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
